import React, { useState } from 'react';
import { FileIcon, DownloadIcon, ExternalLinkIcon, TrashIcon, FileSymlinkIcon, FolderIcon, CalendarIcon, UserIcon, TagIcon, LinkIcon, FolderTreeIcon, DatabaseIcon, ShieldIcon, ClockIcon, EditIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { formatDate, formatDateTime } from '../utils/dateFormatter';
export function FileDetails({
  file,
  onClose,
  onDownload,
  onMove,
  formatFileSize,
  locale = 'en-US'
}) {
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'relationships', 'history'
  const [copied, setCopied] = useState(false);
  // Generate a realistic nanoid-like string
  const generateNanoid = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 21; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };
  // Create a deterministic nanoid-like string based on the file ID
  const getNanoid = fileId => {
    // Use the file ID to seed a deterministic but nanoid-like string
    const seed = fileId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 21; i++) {
      // Use a deterministic approach to generate characters based on the seed
      const charIndex = seed * (i + 1) % chars.length;
      result += chars.charAt(Math.abs(charIndex));
    }
    return result;
  };
  // Generate file URL with the new format and proper nanoid
  const fileUrl = `https://demo.rapidp2p.io/files/${getNanoid(file.id)}`;
  // Handle copy to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(fileUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  // Get file type icon
  const getFileTypeIcon = fileType => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return <FileIcon className="h-5 w-5 text-red-500" data-id="element-4947" />;
      case 'docx':
      case 'doc':
        return <FileIcon className="h-5 w-5 text-blue-500" data-id="element-4948" />;
      case 'xlsx':
      case 'xls':
        return <FileIcon className="h-5 w-5 text-green-500" data-id="element-4949" />;
      case 'pptx':
      case 'ppt':
        return <FileIcon className="h-5 w-5 text-orange-500" data-id="element-4950" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <FileIcon className="h-5 w-5 text-purple-500" data-id="element-4951" />;
      case 'zip':
      case 'rar':
        return <FileIcon className="h-5 w-5 text-gray-500" data-id="element-4952" />;
      default:
        return <FileIcon className="h-5 w-5 text-gray-500" data-id="element-4953" />;
    }
  };
  return <div className="h-full flex flex-col" data-id="element-4954">
      {/* File Header */}
      <div className="border-b border-gray-200 pb-4" data-id="element-4955">
        <div className="flex items-center" data-id="element-4956">
          {getFileTypeIcon(file.type)}
          <h3 className="ml-2 text-lg font-medium text-gray-900 truncate" data-id="element-4957">
            {file.name}
          </h3>
        </div>
        <div className="mt-2 flex items-center text-sm text-gray-500" data-id="element-4958">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-2" data-id="element-4959">
            {file.type.toUpperCase()}
          </span>
          <span data-id="element-4960">{formatFileSize(file.size)}</span>
        </div>
        {/* File URL Section */}
        <div className="mt-3 flex items-center" data-id="element-4961">
          <div className="flex-1 relative" data-id="element-4962">
            <div className="flex items-center bg-gray-50 rounded-md p-2 pr-10 overflow-hidden" data-id="element-4963">
              <LinkIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mr-1.5" data-id="element-4964" />
              <span className="text-xs text-gray-600 truncate" data-id="element-4965">{fileUrl}</span>
            </div>
            <button onClick={copyToClipboard} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-indigo-600" title="Copy URL" data-id="element-4966">
              {copied ? <CheckIcon className="h-4 w-4 text-green-500" data-id="element-4967" /> : <CopyIcon className="h-4 w-4" data-id="element-4968" />}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-4 border-b border-gray-200" data-id="element-4969">
        <nav className="-mb-px flex space-x-8" data-id="element-4970">
          <button onClick={() => setActiveTab('details')} className={`pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'details' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4971">
            Details
          </button>
          <button onClick={() => setActiveTab('relationships')} className={`pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'relationships' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4972">
            Relationships
          </button>
          <button onClick={() => setActiveTab('history')} className={`pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'history' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4973">
            History
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-4 flex-1 overflow-y-auto" data-id="element-4974">
        {activeTab === 'details' && <div className="space-y-4" data-id="element-4975">
            {/* File Info */}
            <div data-id="element-4976">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-4977">
                File Information
              </h4>
              <dl className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-4978">
                <div className="py-3 flex justify-between text-sm" data-id="element-4979">
                  <dt className="text-gray-500" data-id="element-4980">File Name</dt>
                  <dd className="text-gray-900 truncate max-w-[200px]" data-id="element-4981">
                    {file.name}
                  </dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-4982">
                  <dt className="text-gray-500" data-id="element-4983">File Type</dt>
                  <dd className="text-gray-900" data-id="element-4984">{file.type.toUpperCase()}</dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-4985">
                  <dt className="text-gray-500" data-id="element-4986">Size</dt>
                  <dd className="text-gray-900" data-id="element-4987">{formatFileSize(file.size)}</dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-4988">
                  <dt className="text-gray-500" data-id="element-4989">Department</dt>
                  <dd className="text-gray-900" data-id="element-4990">{file.department || 'N/A'}</dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-4991">
                  <dt className="text-gray-500" data-id="element-4992">Status</dt>
                  <dd className="text-gray-900" data-id="element-4993">{file.status || 'Active'}</dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-4994">
                  <dt className="text-gray-500" data-id="element-4995">File ID</dt>
                  <dd className="text-gray-900 font-mono" data-id="element-4996">{file.id}</dd>
                </div>
              </dl>
            </div>

            {/* Dates */}
            <div data-id="element-4997">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-4998">Dates</h4>
              <dl className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-4999">
                <div className="py-3 flex justify-between text-sm" data-id="element-5000">
                  <dt className="text-gray-500" data-id="element-5001">Upload Date</dt>
                  <dd className="text-gray-900" data-id="element-5002">
                    {formatDateTime(file.uploadDate, locale)}
                  </dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5003">
                  <dt className="text-gray-500" data-id="element-5004">Last Modified</dt>
                  <dd className="text-gray-900" data-id="element-5005">
                    {formatDateTime(file.lastModified, locale)}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Tags */}
            {file.tags && file.tags.length > 0 && <div data-id="element-5006">
                <h4 className="text-sm font-medium text-gray-900" data-id="element-5007">Tags</h4>
                <div className="mt-2 flex flex-wrap gap-2" data-id="element-5008">
                  {file.tags.map(tag => <span key={tag} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-5009">
                      <TagIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-5010" />
                      {tag}
                    </span>)}
                </div>
              </div>}

            {/* Storage Information */}
            <div data-id="element-5011">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5012">
                Storage Information
              </h4>
              <dl className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5013">
                <div className="py-3 flex justify-between text-sm" data-id="element-5014">
                  <dt className="text-gray-500" data-id="element-5015">Location</dt>
                  <dd className="text-gray-900 truncate max-w-[200px]" title={file.storageLocation} data-id="element-5016">
                    {file.storageLocation}
                  </dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5017">
                  <dt className="text-gray-500" data-id="element-5018">Access Level</dt>
                  <dd className="text-gray-900" data-id="element-5019">{file.accessLevel}</dd>
                </div>
              </dl>
            </div>
          </div>}

        {activeTab === 'relationships' && <div className="space-y-4" data-id="element-5020">
            {/* Parent Files */}
            <div data-id="element-5021">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5022">
                Parent Files
              </h4>
              {file.parentFiles && file.parentFiles.length > 0 ? <ul className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5023">
                  {file.parentFiles.map(parent => <li key={parent.id} className="py-3 flex items-center justify-between text-sm" data-id="element-5024">
                      <div className="flex items-center" data-id="element-5025">
                        <FolderTreeIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5026" />
                        <span className="text-gray-900" data-id="element-5027">{parent.name}</span>
                      </div>
                      <button className="text-indigo-600 hover:text-indigo-900" data-id="element-5028">
                        <ExternalLinkIcon className="h-4 w-4" data-id="element-5029" />
                      </button>
                    </li>)}
                </ul> : <p className="mt-2 text-sm text-gray-500 italic" data-id="element-5030">
                  No parent files
                </p>}
            </div>

            {/* Child Files */}
            <div data-id="element-5031">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5032">Child Files</h4>
              {file.childFiles && file.childFiles.length > 0 ? <ul className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5033">
                  {file.childFiles.map(child => <li key={child.id} className="py-3 flex items-center justify-between text-sm" data-id="element-5034">
                      <div className="flex items-center" data-id="element-5035">
                        <FileSymlinkIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5036" />
                        <span className="text-gray-900" data-id="element-5037">{child.name}</span>
                      </div>
                      <button className="text-indigo-600 hover:text-indigo-900" data-id="element-5038">
                        <ExternalLinkIcon className="h-4 w-4" data-id="element-5039" />
                      </button>
                    </li>)}
                </ul> : <p className="mt-2 text-sm text-gray-500 italic" data-id="element-5040">
                  No child files
                </p>}
            </div>

            {/* Linked Entities */}
            <div data-id="element-5041">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5042">
                Linked Entities
              </h4>
              {file.linkedEntities && file.linkedEntities.length > 0 ? <ul className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5043">
                  {file.linkedEntities.map(entity => <li key={entity.id} className="py-3 flex items-center justify-between text-sm" data-id="element-5044">
                      <div className="flex items-center" data-id="element-5045">
                        <LinkIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5046" />
                        <span className="text-gray-900" data-id="element-5047">
                          {entity.name}{' '}
                          <span className="text-gray-500" data-id="element-5048">({entity.type})</span>
                        </span>
                      </div>
                      <button className="text-indigo-600 hover:text-indigo-900" data-id="element-5049">
                        <ExternalLinkIcon className="h-4 w-4" data-id="element-5050" />
                      </button>
                    </li>)}
                </ul> : <p className="mt-2 text-sm text-gray-500 italic" data-id="element-5051">
                  No linked entities
                </p>}
            </div>
          </div>}

        {activeTab === 'history' && <div data-id="element-5052">
            <h4 className="text-sm font-medium text-gray-900" data-id="element-5053">File History</h4>
            <ul className="mt-2 border-t border-gray-200 divide-y divide-gray-200" data-id="element-5054">
              <li className="py-3" data-id="element-5055">
                <div className="flex items-center text-sm" data-id="element-5056">
                  <ClockIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5057" />
                  <p className="text-gray-900 font-medium" data-id="element-5058">File modified</p>
                </div>
                <div className="mt-1 flex items-center text-sm text-gray-500" data-id="element-5059">
                  <UserIcon className="h-3 w-3 text-gray-400 mr-1" data-id="element-5060" />
                  <span data-id="element-5061">{file.uploadedBy}</span>
                  <span className="mx-1" data-id="element-5062">•</span>
                  <span data-id="element-5063">{formatDateTime(file.lastModified, locale)}</span>
                </div>
              </li>
              <li className="py-3" data-id="element-5064">
                <div className="flex items-center text-sm" data-id="element-5065">
                  <ClockIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5066" />
                  <p className="text-gray-900 font-medium" data-id="element-5067">File uploaded</p>
                </div>
                <div className="mt-1 flex items-center text-sm text-gray-500" data-id="element-5068">
                  <UserIcon className="h-3 w-3 text-gray-400 mr-1" data-id="element-5069" />
                  <span data-id="element-5070">{file.uploadedBy}</span>
                  <span className="mx-1" data-id="element-5071">•</span>
                  <span data-id="element-5072">{formatDateTime(file.uploadDate, locale)}</span>
                </div>
              </li>
            </ul>
          </div>}
      </div>

      {/* Action Buttons */}
      <div className="mt-6 border-t border-gray-200 pt-4 flex justify-between" data-id="element-5073">
        <div data-id="element-5074">
          <button onClick={() => onDownload(file.id)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-5075">
            <DownloadIcon className="h-4 w-4 mr-1" data-id="element-5076" />
            Download
          </button>
        </div>
        <div className="flex space-x-2" data-id="element-5077">
          <button onClick={() => onMove(file)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-5078">
            <FolderIcon className="h-4 w-4 mr-1" data-id="element-5079" />
            Move
          </button>
          <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-5080">
            <EditIcon className="h-4 w-4 mr-1" data-id="element-5081" />
            Edit
          </button>
        </div>
      </div>
    </div>;
}